import { fetchUsersInfo, getUserPlan, USER_PUBLIC_PICKED_FIELDS } from "@/hooks/user/graphqlUtils";
import { UserDetails } from "@knowt/syncing/graphql/schema";
import { useCurrentUser } from "./useCurrentUser";
import { pick } from "@/utils/dataCleaning";
import { useMemo } from "react";
import { fromEntries } from "@/utils/genericUtils";
import { useSWRImmutable } from "@/hooks/swr/immutable";

export const usePublicProfile = (userId?: string | null, fallbackData: UserDetails = undefined) => {
    const { userId: currentUserId, user } = useCurrentUser();

    const { data: _userData, mutate } = useSWRImmutable(
        userId && userId !== currentUserId && ["publicUserData", userId],
        async () => {
            const usersInfo = await fetchUsersInfo({ userIds: [userId] });
            return usersInfo[userId] ?? null;
        },
        { fallbackData }
    );

    const userData = (userId === currentUserId ? pick(user, ...USER_PUBLIC_PICKED_FIELDS) : _userData) as UserDetails;

    return {
        userData,
        subscriptionType: getUserPlan(userData),
        mutate,
    };
};

export const usePublicProfiles = (userIds?: string[]) => {
    const { userId: currentUserId } = useCurrentUser();

    const { data: rawUsersData } = useSWRImmutable(
        userIds?.length > 0 && ["publicUserData", [...new Set([...userIds])]],
        async () => fetchUsersInfo({ userIds })
    );

    const usersData = useMemo(() => {
        if (!rawUsersData) {
            return null;
        }

        return fromEntries(
            Object.entries(rawUsersData).map(([userId, userData]) => [
                userId,
                userId === currentUserId ? pick(userData, ...USER_PUBLIC_PICKED_FIELDS) : userData,
            ])
        );
    }, [rawUsersData, currentUserId]);

    return { usersData: usersData ?? {} };
};
